import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  FlaskConical,
  GraduationCap,
  Plug,
  Users,
  List,
  Mail,
} from 'lucide-react';
import { isPrototypesEnabled } from '../../config/debug';
import {
  isRowerPeachIntegrationEnabled,
  isRowerSharingFeaturesEnabled,
} from '../../config/featureFlags';
import { getPrototypesForPortal } from '../../features/prototypes/prototypeRegistry';
import { useProfileRoles } from '../../hooks/useProfileRoles';
import { UserRole } from '../../types/database';
import { useAuth } from '../../contexts/AuthContext';
import {
  useRowerProfile,
  useRowerTeams,
  useRowerSessions,
  useRowerAvatarSignedUrl,
} from '../../hooks/useRowerData';
import { teamService } from '../../services/teams/teamService';
import { useQuery } from '@tanstack/react-query';
import {
  rowerRecruitmentProgramSearchParam,
  useSavedSchools,
} from '../../hooks/useRecruitmentData';
import Sidebar from './Sidebar';
import SidebarDrillTransition from './SidebarDrillTransition';
import SidebarUtilityStrip from './SidebarUtilityStrip';
import SidebarProfileMenu from './SidebarProfileMenu';
import RowerProfileCompletionRing from '../rower/profile/RowerProfileCompletionRing';
import {
  computeRowerProfileCompletion,
  rowerProfileToCompletionInput,
} from '../../utils/rowerProfileCompletion';

interface RowerSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/rower', hasSubItems: false },
  { id: 'sessions', label: 'Sessions', icon: List, path: '/rower/sessions', hasSubItems: true },
  { id: 'teams', label: 'Teams', icon: Users, path: '/rower/teams', hasSubItems: true },
  {
    id: 'recruitment',
    label: 'Recruiting / Programs',
    icon: GraduationCap,
    path: '/rower/recruitment',
    hasSubItems: true,
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: Plug,
    path: '/rower/integrations',
    hasSubItems: false,
  },
  {
    id: 'invitations',
    label: 'Invitations',
    icon: Mail,
    path: '/rower/invitations',
    hasSubItems: false,
  },
  {
    id: 'prototypes',
    label: 'Prototypes',
    icon: FlaskConical,
    path: '/rower/prototypes',
    hasSubItems: true,
  },
];

const RowerSidebar: React.FC<RowerSidebarProps> = ({ isCollapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [activeDrillPanel, setActiveDrillPanel] = useState<string | null>(null);

  const { isDeveloper } = useProfileRoles();
  const showPrototypes = isPrototypesEnabled() && isDeveloper;
  const rowerPrototypes = getPrototypesForPortal(UserRole.Athlete);
  const { data: profile } = useRowerProfile();
  const { data: teams = [] } = useRowerTeams(profile?.id || null);
  const { data: sessions = [] } = useRowerSessions(profile?.id || null);
  const avatarSignedUrl = useRowerAvatarSignedUrl(profile?.avatar_url);

  const displayName = (() => {
    const first = (profile?.first_name || '').trim();
    const last = (profile?.last_name || '').trim();
    const fromProfile = [first, last].filter(Boolean).join(' ').trim();
    if (fromProfile) return fromProfile;
    const fromUser = String((user as any)?.user_metadata?.full_name || '').trim();
    if (fromUser) return fromUser;
    return 'Rower';
  })();

  const avatarLetter = useMemo(() => {
    const letter = displayName.trim().charAt(0);
    return letter ? letter.toUpperCase() : 'R';
  }, [displayName]);

  const profileCompletionPercent = useMemo(() => {
    const input = rowerProfileToCompletionInput(profile, { user });
    if (!input) return 100;
    return computeRowerProfileCompletion(input).percent;
  }, [profile, user]);

  const sidebarAvatar = (
    <RowerProfileCompletionRing
      size={32}
      percent={profileCompletionPercent}
      ariaLabel={
        profileCompletionPercent >= 100
          ? undefined
          : `Profile ${profileCompletionPercent}% complete`
      }
    >
      <div className="h-full w-full rounded-full bg-accent text-accent-foreground overflow-hidden flex items-center justify-center font-bold text-sm ring-1 ring-border">
        {avatarSignedUrl ? (
          <img src={avatarSignedUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          avatarLetter
        )}
      </div>
    </RowerProfileCompletionRing>
  );

  const formatShortDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Badge count only — avoid refetch storms on auth hydration / window focus.
  const { data: invitationsData } = useQuery({
    queryKey: ['rower-invitations', user?.id],
    queryFn: async () => {
      const result = await teamService.getInvitations();
      if (result.error) {
        return { invitations: [] };
      }
      return result.data;
    },
    enabled: !!user?.id,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const pendingInvitationsCount = invitationsData?.invitations?.length || 0;

  const { data: savedSchools = [] } = useSavedSchools(user?.id);
  const savedPrograms = useMemo(
    () =>
      [...savedSchools]
        .sort((a, b) => {
          const aTime = a.addedDate ? new Date(a.addedDate).getTime() : 0;
          const bTime = b.addedDate ? new Date(b.addedDate).getTime() : 0;
          return bTime - aTime;
        })
        .slice(0, 3)
        .map((school) => ({ id: school.id, name: school.name })),
    [savedSchools]
  );

  // Get recent 3 sessions and teams
  const recentSessions = sessions
    .sort((a, b) => new Date(b.session_date).getTime() - new Date(a.session_date).getTime())
    .slice(0, 3)
    .map((session) => ({
      id: session.id,
      name: session.session_name || session.session_id || 'Session',
      date: session.session_date,
    }));

  const recentTeams = teams.slice(0, 3);

  const pathname = location.pathname;
  const activeId = pathname.startsWith('/rower/prototypes')
    ? 'prototypes'
    : pathname.startsWith('/rower/sessions')
      ? 'sessions'
      : pathname.startsWith('/rower/teams')
        ? 'teams'
        : pathname.startsWith('/rower/recruitment')
          ? 'recruitment'
          : pathname.startsWith('/rower/integrations')
            ? 'integrations'
            : pathname.startsWith('/rower/invitations')
              ? 'invitations'
              : pathname.startsWith('/rower')
                ? 'dashboard'
                : undefined;
  const showSessions = isRowerPeachIntegrationEnabled();
  const showSharingNav = isRowerSharingFeaturesEnabled();
  const visibleNavItems = navItems.filter((item) => {
    if (item.id === 'prototypes') return showPrototypes;
    if (item.id === 'sessions') return showSessions;
    if (item.id === 'teams' || item.id === 'integrations' || item.id === 'invitations') {
      return showSharingNav;
    }
    return true;
  });

  const drillBackClass =
    'relative w-full flex items-center justify-center p-3 rounded-lg text-sm font-medium text-gray-800 hover:bg-gray-100 transition-colors';
  const drillItemClass =
    'w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700';

  const handleMenuItemClick = (itemId: string, path: string, hasSubItems: boolean) => {
    navigate(path);
    if (!isCollapsed && hasSubItems) {
      setActiveDrillPanel((prev) => (prev === itemId ? null : itemId));
    } else {
      setActiveDrillPanel(null);
    }
  };

  const activePanelMeta = visibleNavItems.find((item) => item.id === activeDrillPanel) || null;

  return (
    <Sidebar
      isCollapsed={isCollapsed}
      onToggle={onToggle}
      navContent={
        <SidebarDrillTransition
          drillKey={!isCollapsed && activePanelMeta ? activeDrillPanel : null}
          drillMenu={
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setActiveDrillPanel(null)}
                className={drillBackClass}
              >
                <ChevronLeft className="absolute left-3 h-4 w-4" aria-hidden />
                <span>{activePanelMeta?.label ?? ''}</span>
              </button>

              {activeDrillPanel === 'sessions' && (
                <ul className="space-y-1">
                  <li className="text-xs font-semibold text-gray-500 uppercase px-3 py-1">
                    Recent Sessions
                  </li>
                  {recentSessions.length === 0 ? (
                    <li className="px-3 py-1 text-xs text-gray-500">No sessions yet</li>
                  ) : (
                    recentSessions.map((session) => (
                      <li key={session.id}>
                        <button
                          onClick={() => navigate(`/rower/sessions/${session.id}`)}
                          className={drillItemClass}
                        >
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="truncate">{session.name}</span>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {formatShortDate(session.date)}
                            </span>
                          </div>
                        </button>
                      </li>
                    ))
                  )}
                  <li className="my-2 border-t border-gray-200"></li>
                  <li>
                    <button
                      type="button"
                      onClick={() => navigate('/rower/sessions')}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors text-rowing-blue font-semibold"
                    >
                      View all sessions
                    </button>
                  </li>
                </ul>
              )}

              {activeDrillPanel === 'prototypes' && (
                <ul className="space-y-1">
                  <li>
                    <button
                      type="button"
                      onClick={() => navigate('/rower/prototypes')}
                      className={`${drillItemClass} ${
                        pathname === '/rower/prototypes'
                          ? 'bg-gray-100 font-semibold text-gray-900'
                          : ''
                      }`}
                    >
                      Prototypes Home
                    </button>
                  </li>
                  <li className="my-2 border-t border-gray-200" />
                  {rowerPrototypes.map((entry) => (
                    <li key={entry.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/rower/prototypes/${entry.id}`)}
                        className={`${drillItemClass} ${
                          pathname === `/rower/prototypes/${entry.id}`
                            ? 'bg-gray-100 font-semibold text-gray-900'
                            : ''
                        }`}
                      >
                        {entry.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {activeDrillPanel === 'teams' && (
                <ul className="space-y-1">
                  <li className="text-xs font-semibold text-gray-500 uppercase px-3 py-1">
                    Recent Teams
                  </li>
                  {recentTeams.length === 0 ? (
                    <li className="px-3 py-1 text-xs text-gray-500">No teams yet</li>
                  ) : (
                    recentTeams.map((team) => (
                      <li key={team.id}>
                        <button onClick={() => navigate('/rower/teams')} className={drillItemClass}>
                          {team.name}
                        </button>
                      </li>
                    ))
                  )}
                  <li className="my-2 border-t border-gray-200"></li>
                  <li>
                    <button
                      type="button"
                      onClick={() => navigate('/rower/teams')}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors text-rowing-blue font-semibold"
                    >
                      View all teams
                    </button>
                  </li>
                </ul>
              )}

              {activeDrillPanel === 'recruitment' && (
                <ul className="space-y-1">
                  <li className="text-xs font-semibold text-gray-500 uppercase px-3 py-1">
                    Saved Programs
                  </li>
                  {savedPrograms.length === 0 ? (
                    <li className="px-3 py-1 text-xs text-gray-500">No programs saved yet</li>
                  ) : (
                    savedPrograms.map((school: { id: string; name: string }) => (
                      <li key={school.id}>
                        <button
                          onClick={() =>
                            navigate(
                              `/rower/recruitment?${rowerRecruitmentProgramSearchParam}=${school.id}`
                            )
                          }
                          className={drillItemClass}
                        >
                          {school.name}
                        </button>
                      </li>
                    ))
                  )}
                  <li className="my-2 border-t border-gray-200"></li>
                  <li>
                    <button
                      type="button"
                      onClick={() => navigate('/rower/recruitment/browse')}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors text-rowing-blue font-semibold"
                    >
                      Browse programs
                    </button>
                  </li>
                </ul>
              )}
            </div>
          }
          rootMenu={
            <ul className="space-y-1">
              {visibleNavItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleMenuItemClick(item.id, item.path, item.hasSubItems)}
                    className={`w-full flex items-center p-3 rounded-lg transition-colors relative ${
                      activeId === item.id
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {!isCollapsed && (
                      <>
                        <span className="font-medium ml-3">{item.label}</span>
                        {item.id === 'invitations' && pendingInvitationsCount > 0 && (
                          <span className="ml-auto bg-accent text-accent-foreground text-xs font-semibold px-2 py-0.5 rounded-full">
                            {pendingInvitationsCount}
                          </span>
                        )}
                        {item.hasSubItems && (
                          <ChevronRightIcon
                            className={`h-4 w-4 ml-auto transition-transform text-gray-500 ${
                              activeDrillPanel === item.id ? 'rotate-90' : ''
                            }`}
                          />
                        )}
                      </>
                    )}
                    {isCollapsed && item.id === 'invitations' && pendingInvitationsCount > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full"></span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          }
        />
      }
      footerContent={
        <div className={`px-3 ${isCollapsed ? 'pt-1.5 pb-0' : 'pt-2 pb-0'}`}>
          <SidebarUtilityStrip
            isCollapsed={isCollapsed}
            persona="rower"
            notificationsPath="/rower/notifications"
            inboxPath="/rower/inbox"
            settingsPath="/rower/settings"
          />
        </div>
      }
      profileContent={
        <SidebarProfileMenu
          isCollapsed={isCollapsed}
          displayName={displayName}
          email={user?.email || ''}
          profilePath="/rower/settings"
          onSignOut={async () => {
            await signOut();
            navigate('/');
          }}
          avatar={sidebarAvatar}
        />
      }
    />
  );
};

export default RowerSidebar;
